import { useGetMainList, useGetOne } from "ra-core";
import { useMemo, useState } from "react";
import { SimpleOption } from "@/components/governance/search/AmbientFacilitiesContext";
import { RecPatch } from "@/dataProvider/tools";
import { EncryptionKeyResponseDto, EncryptionRule, EditFileTemplateInput, FileEncryptionTemplateDto, FileColumn, ObfuscationRuleDto, ObfuscationRuleType, ObfuscationDataType, FileTemplateStatus, DataKindCategory } from "@/gen/api/encrypt";
import { SecondaryClassificationResponseDto } from "@/gen/api/governance";
import { pages, sorts } from "@/utils/paramsWithGetMainList";

export interface OptionsData {
  classifications: SimpleOption[];
  keys: (EncryptionKeyResponseDto & RecPatch)[];
  obfsRules: {
    all: (ObfuscationRuleDto & RecPatch)[];
    maps: Record<ObfuscationDataType, ObfuscationRuleDto[]>;
  };
  clssMap: Record<string, SimpleOption>;
  ruleMap: Record<string, EncryptionRule[]>;
  parquetRuleMap: Record<string, EncryptionRule[]>;
}

export enum FileDataType {
  String = '1',
  Numeric = '2',
  Date = '3',
}

export enum ParquetFileDataType {
  String = "1",
  Int16 = "2",
  Int32 = "3",
  Int64 = "4",
  DateTime = "5",
  Single = "6",
  Double = "7",
  Decimal = "8",
  Boolean = "9",
  ByteArray = "10",
  TimeSpan = "11",
  SByte = "12",
  UInt16 = "13",
  UInt32 = "14",
  UInt64 = "15",
  Byte = "16"
}

export const useOptionsData = (): OptionsData => {
  const { data: keysData } = useGetMainList<EncryptionKeyResponseDto&RecPatch>('settings/encryption/keys', pages(), sorts(), {}, { action: 'CUSTOM_DATA_QUERY' })
  const { data: obfsRulesData } = useGetMainList<ObfuscationRuleDto & RecPatch>('settings/encryption/obfuscation-rule', pages(), sorts(), {}, { action: 'EDIT_OBFS_MAPPING' })
  
  const { data: classificationData } = useGetMainList<SecondaryClassificationResponseDto&RecPatch>('settings/governance/data-classification', pages(), sorts(), {})
  const { data: ruleMapData } = useGetOne<{map:Record<string, EncryptionRule[]>,id:any}>('file-encrypt-rule-map', DataKindCategory.FileTemplate)
  const { data: parquetRuleMapData } = useGetOne<{map:Record<string, EncryptionRule[]>,id:any}>('file-encrypt-rule-map', DataKindCategory.ParquetFile)
  const {classifications, clssMap} = 
    useMemo(() => {
      const classifications: SimpleOption[] = Object.values<SecondaryClassificationResponseDto>(classificationData || {})
        .map(c => ({
          ...c, 
          key: c.secondaryClassificationId || -1, 
          text: `${c.primaryClassificationText} - ${c.name}` || ''
        })).sort((a, b) => 
                ((a.primaryClassification || 0) - (b.primaryClassification || 0)) 
              || ((a.secondaryClassificationId || 0) - (b.secondaryClassificationId || 0)))
      const clssMap = classifications.reduce((acc, c) => ({...acc, [c.key]: c }), {} as Record<string, SimpleOption>)
      return { classifications, clssMap }
    }, [classificationData])
  const keys = useMemo(() => Object.values(keysData || {}).sort((a, b) => a.id - b.id), [keysData])

  const obfsRules = useMemo(() => {
    const allRules = Object.values(obfsRulesData || {})
    const clearDataRule = allRules.find(r => r.ruleType === ObfuscationRuleType.ClearData)
    const obfsRules = allRules.filter(r => r.ruleType !== ObfuscationRuleType.TableColumn && r.ruleType !== ObfuscationRuleType.ClearData)
      .reduce((acc, r) => {
        if (r.dataType != null) {
          const arr = acc[r.dataType] || (acc[r.dataType] = [])
          arr.push(r)
        }
        return acc
      }, {} as Record<ObfuscationDataType, ObfuscationRuleDto[]>)
    Object.values(obfsRules).forEach(arr => {
      (arr as {id:number}[]).sort((a, b) => a.id - b.id)
      clearDataRule && arr.unshift(clearDataRule)
    })
    return { all: allRules, maps: obfsRules }
  }, [obfsRulesData])

  return { classifications, keys, obfsRules, clssMap, ruleMap: ruleMapData?.map || {}, parquetRuleMap: parquetRuleMapData?.map || {} }
}

export interface Pack {
  secondaryClassificationId?: number,
  primaryClassification?: number,
  dataType?: string,
  parquetDataType?: string,
  precision?: number,
  scale?: number,
  encryptionRuleId?: number,
  obfuscationRuleId?: number,
}

export interface MappingEditParams {
  listCtx: any;
  touched: () => boolean;
  hasEncRules: () => boolean;
  gatherEdits: () => EditFileTemplateInput;
  discardAllEdits: () => void;
  keyLens: {
      getKeyId: () => number | "";
      setKeyId: (kId: number) => void;
  };
  valueLens: {
      getValue: (name: string, piece: keyof (Pack)) => string | number | null | undefined;
      setValue: (name: string, piece: keyof (Pack), value: number | string) => void;
      setValues: (name: string, newpack: Pack) => void;
  };
  version: number;
}

export const useMappingEditParams = ({record, onValueEdit}: {record?:FileEncryptionTemplateDto, onValueEdit?: (name:string, column:FileColumn)=>void}): MappingEditParams => {
  const [states, setStates] = useState({
    templateId: -1,
    version: 0,
    encryptionKeyId: record?.encryptionKeyId as number|null,
    mappings: {} as Record<string, Pack>,
  })

  const { listCtx, columnMap } = useMemo(() => {
    setTimeout(() => 
      setStates(v => ({ 
        templateId: record?.templateId || -1,
        version: v.version + 1,
        mappings: {}, 
        encryptionKeyId: record?.encryptionKeyId as any,
      })), 1000)
    const columnMap = (record?.columns || []).reduce((acc, x) => ({...acc, [x.columnName || '']: x}), {} as Record<string, FileColumn>)
    return {
      columnMap,
      listCtx: {
        basePath: '',
        data: columnMap,
        ids: record?.columns?.map(x => x.columnName) || [],
        total: record?.columns?.length || 0,
        loaded: !!record,
        selectedIds: [],
        currentSort: { field: 'order', order: 'ASC', },
        onSelect: () => {},
        onToggleItem: () => {},
        setSort: () => {},
      } as any
    }
  }, [record])

  const getKeyId = () => states.encryptionKeyId || record?.encryptionKeyId || ''
  const setKeyId = (kId: number) => {
    if (states.encryptionKeyId === kId) return;
    setStates(v => ({...v, encryptionKeyId: record?.encryptionKeyId === kId ? null : kId }))
  }

  const getValue = (name:string, piece:keyof(Pack)) => {
    const column = columnMap[name]
    if (!column) { return null }

    const pack = states.mappings[name]
    if (!pack) { return column[piece] } 
    else {
      if (piece === 'precision' || piece === 'scale') {
        return pack[piece] ?? column[piece]
      } else if (piece === 'secondaryClassificationId') {
        return pack[piece] === ('_rm_' as any) ? '' : pack[piece] || column[piece]
      } else {
        return pack[piece] || column[piece]
      }
    }
  }
  const setValue = (name:string, piece:keyof(Pack), value:number|string) => {
    setValues(name, { [piece]: value })
  }
  const setValues = (name:string, newpack: Pack) => {
    const column = columnMap[name]
    if (!column) { return }
    let dirty = false

    setStates(v => {
      const newStates = {...v}
      for(const p in newpack) {
        const piece = p as keyof(Pack)
        const value = newpack[piece]
        
        if (column[piece] === value) {
          if (!v.mappings[name]) { continue }
          else {
            dirty = true
            const pack = v.mappings[name]
            delete pack[piece]
            if (Object.keys(pack).length === 0) {
              delete v.mappings[name]
            }
          }
        } else {
          dirty = true
          const pack = v.mappings[name] || (v.mappings[name] = {})
          pack[piece] = value as any
        }
      }
      const finalStates = dirty ? newStates : v
      if (onValueEdit) {
        onValueEdit(name, assembleColumn(finalStates)(name))
      }
      return finalStates
    })
  }

  const touched = () => {
    if (record?.encryptionKeyId !== states.encryptionKeyId) { return true }
    return Object.keys(states.mappings).length > 0
  }
  const hasEncRules = () => {
    if (!record) { return false }
    if (record.status === FileTemplateStatus.Complete || record.status === FileTemplateStatus.ColumnsLabeled) { return true }
    return Object.values(states.mappings).some(x => x.encryptionRuleId != null && (x.encryptionRuleId as any) !== 'skip')
  }
  const assembleColumn = (s: typeof states) => (k:string) => {
    const column = columnMap[k]
    const pack = s.mappings[k]
    
    return pack ? {...column, ...pack, 
      ...(pack.secondaryClassificationId  === ('_rm_' as any) && { secondaryClassificationId: null }),
      ...(pack.encryptionRuleId  === ('skip' as any) && { encryptionRuleId: null }),
      ...(pack.obfuscationRuleId === ('skip' as any) && { obfuscationRuleId: null }),
    } : {...column} as FileColumn
  }
  const gatherEdits = ():EditFileTemplateInput => ({
    templateId: record?.templateId,
    encryptionKeyId: states.encryptionKeyId,
    columns: Object.keys(states.mappings).map(assembleColumn(states))
  })
  const discardAllEdits = () => {
    setStates(v => ({...v,
      encryptionKeyId: record?.encryptionKeyId as number|null,
      mappings: {},
    }))
  }

  return {
    listCtx,
    touched,
    hasEncRules,
    gatherEdits,
    discardAllEdits,
    keyLens: { getKeyId, setKeyId },
    valueLens: {getValue, setValue, setValues},
    version: states.version,
  }
}
